import React, { useEffect, useMemo, useState } from 'react';
import useCoachingAppointmentStore, {
  isCoachingAppointmentUpcoming,
  parseCoachingLocalDateTime,
} from '../../store/coachingAppointmentStore';
import {
  COACHING_TITLE_PRESETS,
  type CoachingAppointment,
} from '../../types/coachingAppointment';
import type { TeamMemberLike } from '../../utils/teamResolve';
import { openGoogleMeet } from '../../utils/openGoogleMeet';

type Props = {
  clientId: string;
  clientLabel?: string;
  /** Vorbelegung aus resolveClientCoach */
  defaultCoach?: TeamMemberLike | null;
  /** Coach-Auswahl (Team-Pool) */
  coachOptions?: TeamMemberLike[];
  showNotification?: (msg: string, type: 'success' | 'error') => void;
};

const emptyForm = () => ({
  id: '' as string,
  title: 'Kalkulationsgespräch',
  customTitle: '',
  datum: '',
  uhrzeit: '10:00',
  dauerMinuten: '60',
  meetingLink: '',
  coachId: '',
  coachName: '',
  coachAvatarUrl: '',
  notizen: '',
});

const CoachingTerminePanel: React.FC<Props> = ({
  clientId,
  clientLabel,
  defaultCoach,
  coachOptions = [],
  showNotification,
}) => {
  const {
    appointments,
    isLoading,
    error,
    fetchByClientId,
    upsertAppointment,
    deleteAppointment,
  } = useCoachingAppointmentStore();

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (clientId) fetchByClientId(clientId);
  }, [clientId, fetchByClientId]);

  useEffect(() => {
    if (!showForm || form.id) return;
    if (!defaultCoach) return;
    setForm((prev) => ({
      ...prev,
      coachId: String(defaultCoach.id || ''),
      coachName: String(defaultCoach.name || ''),
      coachAvatarUrl: String((defaultCoach as any).imageUrl || ''),
    }));
  }, [showForm, form.id, defaultCoach]);

  const upcoming = useMemo(
    () => appointments.filter((a) => isCoachingAppointmentUpcoming(a)),
    [appointments]
  );
  const past = useMemo(
    () => appointments.filter((a) => !isCoachingAppointmentUpcoming(a)).reverse(),
    [appointments]
  );

  const applyCoach = (coachId: string) => {
    const coach = coachOptions.find((c) => String(c.id) === String(coachId));
    setForm((prev) => ({
      ...prev,
      coachId,
      coachName: coach?.name ? String(coach.name) : prev.coachName,
      coachAvatarUrl: coach ? String((coach as any).imageUrl || '') : prev.coachAvatarUrl,
    }));
  };

  const openCreate = () => {
    const base = emptyForm();
    if (defaultCoach) {
      base.coachId = String(defaultCoach.id || '');
      base.coachName = String(defaultCoach.name || '');
      base.coachAvatarUrl = String((defaultCoach as any).imageUrl || '');
    }
    setForm(base);
    setShowForm(true);
  };

  const openEdit = (ap: CoachingAppointment) => {
    const preset = (COACHING_TITLE_PRESETS as readonly string[]).includes(ap.title)
      ? ap.title
      : 'Sonstiges';
    setForm({
      id: ap.id,
      title: preset,
      customTitle: preset === 'Sonstiges' ? ap.title : '',
      datum: ap.datum,
      uhrzeit: ap.uhrzeit || '10:00',
      dauerMinuten: String(ap.dauerMinuten || 60),
      meetingLink: ap.meetingLink || '',
      coachId: ap.coachId || '',
      coachName: ap.coachName || '',
      coachAvatarUrl: ap.coachAvatarUrl || '',
      notizen: ap.notizen || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    const title =
      form.title === 'Sonstiges'
        ? String(form.customTitle || '').trim()
        : String(form.title || '').trim();
    if (!title || !form.datum || !form.uhrzeit) {
      showNotification?.('Titel, Datum und Uhrzeit sind Pflicht.', 'error');
      return;
    }
    setSaving(true);
    try {
      const dauer = Number(String(form.dauerMinuten).replace(',', '.'));
      await upsertAppointment({
        id: form.id || undefined,
        clientId,
        title,
        datum: form.datum,
        uhrzeit: form.uhrzeit,
        dauerMinuten: Number.isFinite(dauer) && dauer > 0 ? dauer : 60,
        meetingLink: form.meetingLink.trim(),
        coachName: form.coachName.trim() || defaultCoach?.name || 'Coach',
        coachAvatarUrl: form.coachAvatarUrl.trim() || undefined,
        coachId: form.coachId.trim() || undefined,
        notizen: form.notizen.trim() || undefined,
      });
      showNotification?.(form.id ? 'Termin aktualisiert.' : 'Termin angelegt.', 'success');
      setShowForm(false);
      setForm(emptyForm());
    } catch (e: any) {
      console.error(e);
      showNotification?.(e?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ap: CoachingAppointment) => {
    if (!window.confirm(`Termin „${ap.title}" am ${ap.datum} wirklich löschen?`)) return;
    try {
      await deleteAppointment(ap.id);
      showNotification?.('Termin gelöscht.', 'success');
    } catch (e: any) {
      showNotification?.(e?.message || 'Löschen fehlgeschlagen.', 'error');
    }
  };

  const renderRow = (ap: CoachingAppointment) => {
    const dt = parseCoachingLocalDateTime(ap.datum, ap.uhrzeit);
    const dateLabel = dt
      ? dt.toLocaleDateString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : ap.datum;

    return (
      <div
        key={ap.id}
        className="rounded-xl border border-pe-border bg-pe-card p-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-3 min-w-0">
          {ap.coachAvatarUrl ? (
            <img
              src={ap.coachAvatarUrl}
              alt={ap.coachName}
              className="w-10 h-10 rounded-full object-cover border border-pe-border shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#E6C299]/15 border border-[#E6C299]/40 flex items-center justify-center text-[#E6C299] font-black text-xs shrink-0">
              {(ap.coachName || 'C').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-pe-text dark:text-white truncate">{ap.title}</div>
            <div className="text-xs text-pe-muted mt-0.5 font-mono">
              {dateLabel} · {ap.uhrzeit} Uhr
              {ap.dauerMinuten ? ` · ${ap.dauerMinuten} Min.` : ''}
            </div>
            <div className="text-[11px] text-pe-muted mt-1">
              Coach: <span className="text-pe-accent-text font-semibold">{ap.coachName}</span>
            </div>
            {ap.meetingLink && (
              <button
                type="button"
                onClick={() => openGoogleMeet(ap.meetingLink)}
                className="inline-block mt-1 text-[11px] font-bold text-emerald-400 hover:underline truncate max-w-full text-left"
              >
                Meeting-Link öffnen
              </button>
            )}
            {ap.notizen && (
              <p className="text-[11px] text-stone-500 mt-1 line-clamp-2">{ap.notizen}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => openEdit(ap)}
            className="px-3 py-1.5 rounded-lg border border-pe-border text-[10px] font-black uppercase tracking-widest text-pe-muted hover:text-pe-text hover:border-[#E6C299]/50"
          >
            Bearbeiten
          </button>
          <button
            type="button"
            onClick={() => handleDelete(ap)}
            className="px-3 py-1.5 rounded-lg border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 hover:bg-red-500/10"
          >
            Löschen
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#E6C299]/10 border border-[#E6C299]/25 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-sm font-black text-pe-accent-text uppercase tracking-widest">
            1:1 Coaching-Termine
          </h3>
          <p className="text-xs text-pe-muted mt-1 leading-relaxed">
            Individuelle Termine für{' '}
            <strong className="text-pe-text">{clientLabel || 'diesen Mandanten'}</strong>.
            Erscheinen im PEOne-Kalender des Mandanten — getrennt von Gruppen-Events.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-4 py-2.5 bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-lg transition-colors shrink-0"
        >
          + Neuer Termin
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {showForm && (
        <div className="rounded-xl border border-pe-border-strong bg-pe-card p-5 space-y-4">
          <h4 className="text-xs font-black uppercase tracking-widest text-pe-muted">
            {form.id ? 'Termin bearbeiten' : 'Neuen Termin anlegen'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Titel
              </label>
              <select
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
              >
                {COACHING_TITLE_PRESETS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            {form.title === 'Sonstiges' && (
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                  Eigener Titel
                </label>
                <input
                  value={form.customTitle}
                  onChange={(e) => setForm((p) => ({ ...p, customTitle: e.target.value }))}
                  className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
                  placeholder="z.B. Quartals-Review"
                />
              </div>
            )}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Datum
              </label>
              <input
                type="date"
                value={form.datum}
                onChange={(e) => setForm((p) => ({ ...p, datum: e.target.value }))}
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Uhrzeit
              </label>
              <input
                type="time"
                value={form.uhrzeit}
                onChange={(e) => setForm((p) => ({ ...p, uhrzeit: e.target.value }))}
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Dauer (Minuten)
              </label>
              <input
                type="number"
                min={15}
                step={15}
                value={form.dauerMinuten}
                onChange={(e) => setForm((p) => ({ ...p, dauerMinuten: e.target.value }))}
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Meeting-Link
              </label>
              <input
                type="url"
                value={form.meetingLink}
                onChange={(e) => setForm((p) => ({ ...p, meetingLink: e.target.value }))}
                placeholder="https://meet.google.com/…"
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Coach
              </label>
              {coachOptions.length > 0 ? (
                <select
                  value={form.coachId}
                  onChange={(e) => applyCoach(e.target.value)}
                  className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
                >
                  <option value="">— Coach wählen —</option>
                  {coachOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={form.coachName}
                  onChange={(e) => setForm((p) => ({ ...p, coachName: e.target.value }))}
                  className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
                  placeholder="Coach-Name"
                />
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black uppercase tracking-widest text-pe-muted mb-1">
                Notizen (intern)
              </label>
              <textarea
                value={form.notizen}
                onChange={(e) => setForm((p) => ({ ...p, notizen: e.target.value }))}
                rows={2}
                className="w-full bg-pe-bg border border-pe-border-strong text-pe-text rounded-lg px-3 py-2.5 text-sm"
                placeholder="Optional…"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm(emptyForm());
              }}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-pe-muted"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="px-5 py-2.5 bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-lg disabled:opacity-50"
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-pe-muted animate-pulse">Lade Termine…</p>
      ) : appointments.length === 0 ? (
        <p className="text-sm text-pe-muted border border-dashed border-pe-border rounded-xl p-6 text-center">
          Noch keine 1:1-Termine für diesen Mandanten.
        </p>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                Bevorstehend ({upcoming.length})
              </h4>
              {upcoming.map(renderRow)}
            </div>
          )}
          {past.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-500">
                Vergangen ({past.length})
              </h4>
              {past.map(renderRow)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachingTerminePanel;
